import { ValueAxis } from '@amcharts/amcharts5/xy';
import { AxisRendererCircular } from '@amcharts/amcharts5/radar';
import { color, p50, percent } from '@amcharts/amcharts5';
import { Constant, LABEL_PLACEHOLDERS } from '@/modules/core/legacy/charts/am5/charts.constants';
import { useTitle } from '@/modules/core/legacy/charts/am5/bar/composables/useTitle';
import { useRadarHand } from '@/modules/core/legacy/charts/am5/gauge/composables/useRadarHand';
import { HandTypes } from '@/modules/ta/legacy/widget/widget.constants';
import { useAxisLessTooltip } from '@/modules/core/legacy/charts/am5/gauge/composables/useAxisLessTooltip';
import { GaugeHand } from '@/modules/core/legacy/charts/am5/gauge/models/GaugeHand';
import { useNumberFormatter } from '@/modules/core/legacy/charts/am5/gauge/composables/useNumberFormatter';
import { useAxisRange } from '@/modules/core/legacy/charts/am5/gauge/composables/useAxisRange';
import { useToolTips } from '@/modules/core/legacy/charts/am5/pie/composables/useTooltips';

export function useGaugeAxes(context) {
  const { root, config, isDarkTheme } = context();
  const { addTitle } = useTitle(context);
  const { createRadarHand } = useRadarHand(context);
  const { addCursor, createTooltip } = useAxisLessTooltip(context);
  const { formatNumber, getAxisFormatter } = useNumberFormatter(context);
  const { addAxisRange } = useAxisRange(context);

  function addChartAxes() {
    if (!config.value?.axisRanges?.length) {
      return;
    }

    addAxis(config.value.axisRanges, getRenderer());
    if (config.value.comparisonEnabled) {
      addAxis(config.value.comparisonAxisRanges, getRenderer(), true);
    }

    // labels are the top and bottom text that say what we're measuring
    addLabels();

    // add any chart titles if it's in comparison
    addTitles();
  }

  /**
   * Titles are the "current" and "prior" period labels right at the top
   */
  function addTitles() {
    if (!config.value.comparisonEnabled) {
      return;
    }

    const labelSettings = {
      fontSize: Constant.ONE_EM,
      fontWeight: 600,
      marginTop: -20,
      x: p50,
    };

    shiftLabel(labelSettings, false);
    shiftLabel(labelSettings, true);
  }

  /**
   * Unshift a label into the top axes container
   * @param labelSettings
   * @param isComparison
   */
  function shiftLabel(labelSettings, isComparison = false) {
    const label = addTitle(
      i18n.$t(isComparison ? Constant.PRIOR_PERIOD : Constant.CURRENT_PERIOD),
      labelSettings
    );
    const chartValue = getChartValue(isComparison);
    chartValue.topAxesContainer.children.unshift(label);
  }

  /**
   * Builds out our circular renderer with default settings
   * Also controls the labels around the outside and the ticks
   * @returns {InstanceType<typeof Entity>}
   */
  function getRenderer() {
    const colorSettings = {};
    const renderer = AxisRendererCircular.new(root.value, {
      innerRadius: percent(config.value.getGaugeThickness()),
      ...colorSettings,
    });

    // hide the grid line always
    renderer.grid.template.setAll({
      forceHidden: true,
    });

    // ticks are enabled sometimes
    renderer.ticks.template.setAll({
      strokeOpacity: 1,
      minPosition: 0,
      visible: config.value.showTicks,
      inside: false,
      length: 5,
    });

    // these are the auto-generated labels that go around the outside
    if (!config.value.showTicks) {
      renderer.labels.template.setAll({
        forceHidden: true,
      });
    }

    return renderer;
  }

  /**
   * The axis in this case is a gauge showing how much the chart is filled
   * @param axisRange
   * @param renderer
   * @param isComparison
   * @returns {number}
   */
  function addAxis(axisRange, renderer, isComparison = false) {
    const { assignThemeColor } = useToolTips(context);
    const fontColorPicker = assignThemeColor(isDarkTheme, config.value);
    const chartValue = getChartValue(isComparison);
    const initialRange = axisRange[0];
    const targetRange = axisRange[1];
    const maxValue = targetRange.endValue;
    const numberFormat = getAxisFormatter(targetRange.format);
    const targetEndValue = formatNumber(maxValue, targetRange.format);

    const axisSettings = {
      min: 0,
      max: maxValue,
      strictMinMax: true,
      renderer,
      ...numberFormat,
    };

    if (config.value.useTooltip) {
      const endValue = formatNumber(initialRange.endValue, initialRange.format);

      axisSettings.tooltip = createTooltip({
        color: initialRange.fill,
        tooltipText: `${Constant.BOLD}${config.value.labels[1].text}: ${endValue}[/] (${config.value.labels[0].text} : ${LABEL_PLACEHOLDERS.TOOLTIP_VALUE})`,
        isComparison,
      });
    }

    const axis = chartValue.xAxes.push(ValueAxis.new(root.value, axisSettings));
    // Give fontcolor for value axis labels
    axis.get(Constant.RENDERER).labels.template.set(Constant.FILL, color(fontColorPicker));

    // gauge charts don't have a traditional series so we need to use the cursor as a way to
    // trigger the tooltip
    if (config.value.useTooltip) {
      addCursor(chartValue);
    }

    const initialDataItem = addAxisRange(axis, initialRange, maxValue, isComparison);
    const targetDataItem = addAxisRange(axis, targetRange, maxValue, isComparison);

    if (config.value.showLabel && !config.value.showTicks) {
      // we've removed all the auto-generated labels so replace them
      // one at the start and one at the end of the ranges
      addAxisLabels(initialDataItem, 0);
      addAxisLabels(targetDataItem, targetEndValue, false);
    }

    if (config.value.canShowGaugeValue()) {
      addMetricValue(initialRange, isComparison);
    }

    const handColor = config.value.gaugeHandCustomColor
      ? config.value.handColor
      : initialRange.fill;
    createRadarHand(
      getChartValue(isComparison),
      axis,
      new GaugeHand({
        value: initialRange.endValue,
        comparativeValue: targetRange.endValue,
        fill: handColor,
        isComparison,
        bigNumber: initialRange.bigNumber,
      })
    );

    return axis;
  }

  /**
   * Labels are the metric names that appear above and below
   */
  function addLabels() {
    addAndCreateLabels(config.value.labels);

    if (config.value.comparisonEnabled) {
      addAndCreateLabels(config.value.comparisonLabels, true);
    }
  }

  function addAndCreateLabels(labelElement, isComparison = false) {
    if (labelElement.length) {
      if (labelElement[0]) {
        createLabel(labelElement[0], false, isComparison);
      }
      if (labelElement[1]) {
        createLabel(labelElement[1], true, isComparison);
      }
    }
  }

  /**
   * Set up where the label is located with some default settings
   * @param titleElement
   * @param topPosition
   * @param isComparison
   */
  function createLabel(titleElement, topPosition = true, isComparison = false) {
    let marginBottom = isComparison ? 70 : 40;
    if (!config.value.gaugeShowValue) {
      marginBottom -= 40;
    }

    if (config.value.comparisonEnabled) {
      marginBottom -= isComparison ? 40 : 30;
    }

    if (config.value.bigNumberIsBelowGauge() || config.value.bigNumberIsInsidePointer()) {
      marginBottom -= isComparison ? 50 : 40;
    }

    const bottomYPos = config.value.gaugeHandType === HandTypes.NONE ? 50 : 100;

    const labelSettings = {
      x: p50,
      y: topPosition ? percent(0) : percent(bottomYPos),
      fontSize: Constant.ONE_POINT_ONE,
      marginBottom: topPosition ? marginBottom : 0,
      marginTop: topPosition ? 0 : 10,
    };

    if (titleElement.fill) {
      labelSettings.fill = color(titleElement.fill);
    }

    addLabelToChart(titleElement.text, labelSettings, topPosition, isComparison);
  }

  /**
   * Metric value is the value of the metric that appears in the middle
   * @param axisRange
   * @param isComparison
   */
  function addMetricValue(axisRange, isComparison = false) {
    const yPos = isComparison ? 75 : 70;

    addLabelToChart(
      formatNumber(axisRange.endValue, axisRange.format),
      {
        fill: color(axisRange.fill),
        fontSize: Constant.ONE_POINT_TWO,
        fontWeight: 400,
        y: percent(config.value.gaugeHandType === HandTypes.NONE ? 10 : yPos),
        x: p50,
        populateText: true,
        value: axisRange.endValue,
      },
      false,
      isComparison
    );
  }

  /**
   * The gauge chart is actually two charts so instead of pulling from {chart.value}
   * we need to get it from the root container's children. 0 for current and 1 for prior
   * @param isComparison
   * @returns {*}
   */
  function getChartValue(isComparison) {
    const chartIndex = isComparison ? 1 : 0;
    return root.value.container.children._values[chartIndex];
  }

  /**
   * Pushes a label into the chart axes container
   * @param text
   * @param settings
   * @param topPosition
   * @param isComparison
   */
  function addLabelToChart(text, settings, topPosition = true, isComparison = false) {
    const { assignThemeColor } = useToolTips(context);
    const fontColorPicker = assignThemeColor(isDarkTheme, config.value);

    const label = addTitle(`[${fontColorPicker}]${text}`, settings);
    const chartValue = getChartValue(isComparison);

    if (topPosition) {
      chartValue.topAxesContainer.children.push(label);
    } else {
      chartValue.bottomAxesContainer.children.push(label);
    }
  }

  /**
   * Add the start / finish values to the left / right of the gauge
   * @param dataItem
   * @param value
   * @param leftPosition
   */
  function addAxisLabels(dataItem, value, leftPosition = true) {
    const { assignThemeColor } = useToolTips(context);
    const fontColorPicker = assignThemeColor(isDarkTheme, config.value);
    dataItem.get(Constant.LABEL).setAll({
      forceHidden: false,
      location: leftPosition ? 0 : 1,
      paddingLeft: leftPosition ? 0 : 10,
      paddingRight: leftPosition ? 10 : 0,
      populateText: true,
      text: `[${fontColorPicker}]${value}`,
      textType: Constant.ADJUSTED,
    });
  }

  return {
    addChartAxes,
    getChartValue,
  };
}
